import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Configuration } from 'src/app/models/configurators-2d/applications/carpentry/configuration';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ModalService } from 'src/app/services/modal.service';
import { TunnelService } from 'src/app/services/tunnel.service';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { MODAL } from 'src/app/services/modal.service';
import { CarpentryTunnelStore } from '../carpentry-tunnel.store';
import { CurrentService } from 'src/app/services/current.service';
import { isCarpentryProjectLocked } from 'src/app/services/helpers/carpentry-order-state.helper';
import { logError } from 'src/app/services/helpers/sentry.helper';

const ERROR_TO_IGNORE = 'ignore';

@Component({
  selector: 'ng-casto-carpentry-tunnel-products',
  templateUrl: './carpentry-tunnel-products.component.html',
  styleUrl: './carpentry-tunnel-products.component.scss',
})
export class CarpentryTunnelProductsComponent extends BaseTunnelObject implements OnInit, OnDestroy {
  // Services
  public tunnelService = inject(TunnelService);
  protected service = inject(CarpentryService);
  protected modalService = inject(ModalService);
  private readonly customerService = inject(CustomerService);
  private route = inject(ActivatedRoute);

  protected store = inject(CarpentryTunnelStore);
  private readonly currentService = inject(CurrentService);

  protected configuration: Configuration;
  protected expandedRows: { [key: string]: boolean } = {};

  get isLocked(): boolean {
    return (this.currentService.HBMProject?.locked ?? false) || isCarpentryProjectLocked(this.configuration);
  }

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initTunnelData();
  }

  private async initTunnelData(): Promise<void> {
    this.store.loader = true;
    try {
      // Charger le client dans le store tunnel
      await this.tunnelDataStore.loadCurrentCustomer(this.customerService.userDatas);

      // Si un ID est passé dans l'URL (réouverture d'un projet existant) et que la config
      // en mémoire n'est pas déjà celle du projet, charger la configuration depuis le backend.
      const configId = this.route.snapshot.params['startIndex'];
      if (configId && this.service.configuration.id !== configId) {
        await this.service.getProjectById(configId, true);
        this.service.recreateQuotationRecords();
      }

      // Mettre à jour le store avec la configuration courante
      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.service.configuration);

      // Si la configuration a déjà un tunnel (projet existant), charger les données tunnel
      if (this.service.configuration.tunnelId) {
        await this.service.loadExistingTunnelData();
      }
    } finally {
      this.store.loader = false;
    }
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.configuration = configurationData.get(CONFIG_CONTEXT.CARPENTRY) as Configuration;
          this.store.setConfiguration(this.configuration);
        }
      })
    );
    this.store.loadDiscounts();
  }

  // ── Cycle de vie tunnel ───────────────────────────────────────────────────

  /** Appelé par l'action-bar avant de passer à l'étape suivante. */
  protected async beforeNextAction(): Promise<void> {
    if (this.service.configuration.tunnelId && this.customerService.userDatas) {
      await this.service.saveProject();
      return;
    }

    const proceed = async () => {
      await this.tunnelDataStore.loadCurrentCustomer(this.customerService.userDatas);
      await this.service.loadOrCreateConfiguration();
      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.service.configuration);
    };

    if (this.customerService.userDatas) {
      return proceed();
    }

    return this.selectCustomerIfNeeded(proceed);
  }

  /** Met à jour le titre du projet. */
  protected async onTitleChange(title: string): Promise<void> {
    try {
      await this.service.updateTitle(title);
    } catch (error) {
      logError(error);
    }
  }

  private selectCustomerIfNeeded(callback: () => Promise<void>): Promise<void> {
    if (this.customerService.userDatas) {
      return callback();
    }

    return new Promise<void>((resolve, reject) => {
      const onSuccess = async () => {
        this.modalService.close(true);
        try {
          await callback();
          resolve();
        } catch (err) {
          reject(err);
        }
      };

      const onClose = () => {
        if (this.modalService.returnValue !== true) {
          reject(ERROR_TO_IGNORE);
        }
      };

      this.modalService.display(MODAL.SEARCH_CUSTOMER, { onCustomerSelect: onSuccess }, false, onClose);
    });
  }

}
